declare global {
    interface Window {
        map: MapboxMap;
        isMapIdle: boolean;
    }
}

import 'mapbox-gl/dist/mapbox-gl.css';

import GLError from '@map/GLError';
import {sendGLNotSupportedEvent} from '@map/Map/mapAnalyticsEvents';
import {supported} from '@mapbox/mapbox-gl-supported';
import {Portal} from '@mui/material';
import {
    AttributionControl,
    LngLatLike,
    MapboxOptions,
    Style,
    TransformRequestFunction,
} from 'mapbox-gl';
import mapboxgl from 'mapbox-gl';
import {type ReactNode, useEffect, useRef} from 'react';

import classes from './Mapbox.module.css';

export type MapboxImage = Parameters<InstanceType<typeof mapboxgl.Map>['addImage']>[1];

export interface ControlPointPopup {
    renderChildren: () => ReactNode;
    lngLat: LngLatLike;
}

export interface MapboxProps {
    onMapboxRefChanged: (map: MapboxMap) => void;
    mapboxStyle: Style;
    options: Partial<MapboxOptions> | undefined;
    popupComponent: ReactNode;
    popupContainer: HTMLElement;
    transformRequest: TransformRequestFunction;
    sidebarType: SidebarState['type'];
    center: LngLatLike | undefined;
    zoom: number | undefined;
}

export default function Mapbox(props: MapboxProps) {
    const glSupportedRef = useRef(supported());
    const containerRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<MapboxMap>();

    const propsRef = useRef(props);

    useEffect(() => {
        propsRef.current = props;
    });

    useEffect(() => {
        if (!glSupportedRef.current) sendGLNotSupportedEvent();

        if (!containerRef.current) throw new Error('Mapbox container ref is not set!');

        const {
            options,
            center,
            zoom,
            sidebarType,
            onMapboxRefChanged,
            mapboxStyle: style,
            transformRequest,
        } = propsRef.current;

        const {current: container} = containerRef;

        const map = new mapboxgl.Map({
            ...options,
            ...(center && {center}),
            ...(zoom && {zoom}),
            container,
            style,
            transformRequest,
            minPitch: 0,
            maxPitch: 80,
            optimizeForTerrain: false,
            refreshExpiredTiles: false,
            attributionControl: false, // remove the default...
        });

        mapRef.current = map;

        // ...so that we can add it back with `compact: true`
        if (sidebarType !== 'print') {
            map.addControl(new AttributionControl({compact: true}), 'bottom-right');
        }

        onMapboxRefChanged(map);

        if (DEPLOYMENT !== 'production') {
            window.map = map;

            // The rest of this block is for the purposes of visual regression testing. In order to
            //  obtain reliably reproducable screenshots, the 'idle', 'render', and 'dataloading'
            // events are watched to determine when the map is in a stable state.

            let timeout: ReturnType<typeof setTimeout>;

            window.map.on('idle', () => {
                timeout = setTimeout(() => {
                    window.isMapIdle = true;
                }, 5000);
                // The 5000 millisecond delay avoids transient idle states
            });

            window.map.on('render', () => {
                // Interrupt the timeout if more rendering occurs
                clearTimeout(timeout);
                if (window.isMapIdle) {
                    window.isMapIdle = false;
                }
            });

            window.map.on('data', () => {
                // Interrupt the timeout if more data is requested
                clearTimeout(timeout);
                if (window.isMapIdle) {
                    window.isMapIdle = false;
                }
            });
        }

        return () => {
            mapRef.current?.remove();
        };
    }, []);

    return (
        <>
            {mapRef.current && !glSupportedRef.current && <GLError />}
            <div ref={containerRef} className={classes.mapbox} key='mapbox' />
            {props.popupComponent && (
                <Portal key='popup' container={props.popupContainer}>
                    {props.popupComponent}
                </Portal>
            )}
        </>
    );
}
